<template>
  <li
    ref="container"
    class="cart-drawer-upsell-item list-item"
    :class="upsell.key"
    role="group"
    aria-label="upsell item"
  >
    <img
      class="cart-drawer-upsell-item-image"
      :src="upsellImage ? upsellImage.src : null"
      :alt="upsellImage ? upsellImage.alt : productOptionVariantTitle"
    />

    <div class="cart-drawer-upsell-item-title">
      <h4>
        {{ upsellTitle }}
        <span v-if="productOptionVariantTitle">
          - {{ productOptionVariantTitle }}</span
        >
      </h4>
    </div>

    <div
      class="cart-drawer-upsell-item-information"
      v-if="upsell.gift_with_purchase"
    >
      <div class="cart-drawer-upsell-item-title">
        <h4>{{ upsellTitle }}</h4>
      </div>

      <div
        class="cart-drawer-upsell-item-variant-option"
        v-if="productOptionVariantTitle"
      >
        <span>Color: </span> <span>{{ productOptionVariantTitle }}</span>
      </div>

      <div class="cart-drawer-upsell-item-info">
        <p v-html="upsell.description"></p>
        <div class="cart-drawer-upsell-item-prices">
          <div
            class="cart-drawer-upsell-item-compare-at-price"
            v-if="upsellCompareAtPrice"
            role="text"
          >
            <span class="sr-only-text">Original price:</span>
            <span>{{ upsellCompareAtPrice }}</span>
          </div>

          <div
            v-if="upsellPrice"
            class="cart-drawer-upsell-item-price"
            :class="{ discount: upsellCompareAtPrice }"
            role="text"
          >
            <span class="sr-only-text">Current price:</span>
            <span>{{ upsellPrice }}</span>
          </div>
        </div>

        <button
          class="gwp-learn-more"
          v-if="learnMoreVariant"
          @click="toggleLearnMoreModal"
        >
          Learn More
        </button>
      </div>

      <button
        @click="addUpsellsToCart"
        class="cart-drawer-upsell-item-cta"
        :class="{ loading }"
      >
        {{ loading ? "ADDED!" : "+ ADD" }}
      </button>
    </div>

    <fragment v-else>
      <div class="cart-drawer-upsell-item-description">
        <p v-html="upsell.description"></p>
      </div>

      <div class="cart-drawer-upsell-item-bottom">
        <div class="cart-drawer-upsell-item-prices">
          <div
            class="cart-drawer-upsell-item-compare-at-price"
            v-if="upsellCompareAtPrice"
            role="text"
          >
            <span class="sr-only-text">Original price:</span>
            <span>{{ upsellCompareAtPrice }}</span>
          </div>

          <div
            class="cart-drawer-upsell-item-price"
            :class="{ discount: upsellCompareAtPrice }"
            role="text"
          >
            <span class="sr-only-text">Current price:</span>
            <span>{{ upsellPrice }}</span>
          </div>
        </div>

        <button
          @click="addUpsellsToCart"
          class="cart-drawer-upsell-item-cta"
          :class="{ loading }"
        >
          {{ loading ? "ADDED!" : "+ ADD" }}
        </button>
      </div>
    </fragment>
  </li>
</template>

<!-- eslint-disable camelcase -->
<script>
import { mapGetters, mapState } from "vuex";
import { handleize } from "@/lib/utils";
import { formatMoney, currencySymbol } from "@/lib/currency";
import {
  crossSellAddedEvent,
  crossSellPresentedEvent,
} from "../../api/analytics";
export default {
  name: "cart-drawer-upsell-item",
  data() {
    return {
      loading: false,
    };
  },
  props: {
    upsell: Object,
    index: Number,
    productTitle: String,
  },
  mounted() {
    const upsellItems =
      document.querySelectorAll(".cart-drawer-upsell-item") || [];
    const index = [...upsellItems].findIndex(
      (item) => item === this.$refs.container
    );
    const testVariant = document.documentElement.classList.contains(
      "show-3-upsell"
    )
      ? "3-up"
      : "1.5up";
    crossSellPresentedEvent(
      this.upsell.key,
      index,
      this.upsell.price,
      testVariant
    );
  },
  methods: {
    async addUpsellsToCart() {
      const cartData = this.$store.state.cart.data;
      const productVariants = this.productVariants || [];
      const {
        price,
        gift_with_purchase,
        key,
        required_product_ids,
        required_variant_ids,
        or,
        gwp_ignore_limit,
        variants,
      } = this.upsell;
      const variantItems =
        variants.map((variantUpsell) => variantUpsell.variant) || [];
      const upsellVariants = productVariants
        .concat(variants)
        .map((variant) => ({
          id: variant.id,
          properties: {
            _upsell_active: key,
            _upsell: {
              key,
              requiredProducts: required_product_ids,
              requiredVariants: required_variant_ids,
              or,
            },
            _created_at: Date.now(),
            _reactive:
              gift_with_purchase && !gwp_ignore_limit
                ? this.getReactiveItem()
                : null,
          },
        }));
      this.loading = true;
      const updatedCart = await this.$store.dispatch(
        "cart/addToCart",
        upsellVariants
      );
      const testVariant = document.documentElement.classList.contains(
        "show-3-upsell"
      )
        ? "3-up"
        : "1.5up";
      crossSellAddedEvent(key, this.index, price, testVariant);

      // fire event with maestro
      const upsellsAdded = productVariants.concat(variantItems);
      const user = window.theme.customer;
      const eventData = {
        "cart token": updatedCart.token,
        "items in cart - product titles": updatedCart.items.map(
          (item) => item.product_title
        ),
        "items in cart - variant titles": updatedCart.items.map(
          (item) => item.variant_title
        ),
        "items in cart - variant ids": updatedCart.items.map(
          (item) => item.variant_id
        ),
        "items in cart - prices": updatedCart.items.map((item) =>
          (item.final_line_price / 100).toFixed(2)
        ),
        "items in cart - skus": updatedCart.items.map((item) => item.sku),
        "upsells added - product titles": this.productTitle.split("|"),
        "upsells added - variant titles": upsellsAdded.map((item) => item.name),
        "upsells added - variant ids": upsellsAdded.map((item) => item.id),
        "upsells added - prices": upsellsAdded.map((item) => {
          const lineItem = updatedCart.items.find(
            (_item) => _item.variant_id === item.id
          );
          return lineItem ? (lineItem.final_price / 100).toFixed(2) : null;
        }),
        "upsells added - skus": upsellsAdded.map((item) => item.sku),
        "customer - logged in": user.isLoggedIn,
        "customer - shopify id": user.id,
        "customer - email": user.email ? user.email : "",
        "customer - total orders":
          user.orders_count || user.orders_count === 0 ? user.orders_count : "",
        "customer - total spent":
          user.total_spent || user.total_spent === 0 ? user.total_spent : "",
      };

      window.maestro.fireTrackEvent("clickAddFromSlideout", eventData);
      this.loading = false;
    },
    getReactiveItem() {
      return [
        ...this.upsell.required_product_ids,
        ...this.upsell.required_variant_ids,
      ];
    },
    toggleLearnMoreModal() {
      const toggleModalEvent = new CustomEvent("product-modal:open", {
        detail: this.learnMoreVariant,
      });
      window.dispatchEvent(toggleModalEvent);
    },
  },
  computed: {
    ...mapState({
      cart: (state) => state.cart.data,
      variantsMetafields: (state) => state.metafields.variants,
    }),
    ...mapGetters({
      getVariantLearnMore: "metafields/getVariantLearnMore",
      getVariantsFromProductUpsells: "metafields/getVariantsFromProductUpsells",
      optionsScoreMap: "cart/optionsScoreMap",
      getUpsellPrices: "metafields/getUpsellPrices",
    }),
    learnMoreVariant() {
      return this.getVariantLearnMore(this.upsell.learn_more_variant);
    },
    productVariants() {
      return this.getVariantsFromProductUpsells(
        this.upsell,
        this.optionsScoreMap
      );
    },
    upsellPrice() {
      const { price } = this.getUpsellPrices(this.upsell);
      if (!price) return null;
      return `${currencySymbol()}${formatMoney(
        price,
        "{{ amount_no_trailing_zeroes }}",
        true
      )}`;
    },
    upsellCompareAtPrice() {
      const { compareAtPrice } = this.getUpsellPrices(this.upsell);
      if (!compareAtPrice) return null;
      return `${currencySymbol()}${formatMoney(
        compareAtPrice,
        "{{ amount_no_trailing_zeroes }}",
        false
      )}`;
    },
    upsellImage() {
      const upsell = this.upsell;
      if (!upsell || !upsell.image || !upsell.image.length) return null;
      if (this.productVariants) {
        const optionHandle = handleize(this.productOptionVariantTitle || "");
        const variantImage = upsell.image.find(
          ({ alt }) => alt === optionHandle
        );
        return variantImage ? variantImage : upsell.image[0];
      }

      if (this.optionsScoreMap) {
        for (let i = 0; i < this.optionsScoreMap.length; i++) {
          const image = upsell.image.find(
            ({ alt }) =>
              handleize(alt) === handleize(this.optionsScoreMap[i].name)
          );
          if (image) return image;
        }
      }

      return upsell.image[0];
    },
    productOptionVariantTitle() {
      if (!this.productVariants || !this.productVariants.length) return null;
      if (
        this.productVariants.length > 1 ||
        this.productVariants[0].title === "Default Title"
      )
        return null;
      return this.productVariants[0].option1;
    },
    upsellTitle() {
      if (this.upsell.title) {
        return this.upsell.title;
      } else if (this.upsell.products.length) {
        return this.upsell.products[0] && this.upsell.products[0].title;
      } else {
        return this.upsell.variants[0] && this.upsell.variants[0].variant.name;
      }
    },
  },
};
</script>
